/** @module map-hash */

let markerHashActive = false;

/** Write a marker permalink to the URL hash and lock map-position updates.
 * @param {string} type - OSM type: node | way | relation
 * @param {string|number} id - OSM element ID
 */
export function setMarkerHash(type, id) {
  markerHashActive = true;
  history.replaceState(null, "", `#${type}/${id}`);
}

/** Parse a marker permalink from the current URL hash.
 * @returns {{type: string, id: string}|null}
 */
export function parseMarkerHash() {
  const match = /^#(?<type>node|way|relation)\/(?<id>\d+)$/u.exec(location.hash);
  return match ? { type: match.groups.type, id: match.groups.id } : null;
}

/** Sync map position with URL hash (#map=zoom/lat/lng).
 * Supports a marker permalink format (#node/id, #way/id, #relation/id);
 * while a marker hash is active, moveend will not overwrite the URL.
 * Falls back to localStorage if no hash is present on load.
 * @param {import('leaflet').Map} map - Leaflet map instance
 * @returns {{syncHash: Function}} syncHash() clears any active marker hash and updates the URL to the current map position
 */
export function createMapHash(map) {
  function setHash() {
    if (markerHashActive) { return; }
    const center = map.getCenter();
    const zoom = map.getZoom();
    const precision = Math.max(0, Math.ceil(Math.log(zoom) / Math.LN2));
    const hashStr = `#map=${zoom}/${center.lat.toFixed(precision)}/${center.lng.toFixed(precision)}`;
    if (location.hash !== hashStr) {
      history.replaceState(null, "", hashStr);
    }
    // Always update localStorage to remember last position
    localStorage.setItem("veggiekarte_map_position", hashStr);
  }

  function parsePosition(hashString) {
    if (!hashString || !hashString.startsWith("#map=")) {
      return null;
    }
    const parts = hashString.slice(5).split("/");
    if (parts.length === 3) {
      const zoom = Number.parseInt(parts[0], 10);
      const lat = Number.parseFloat(parts[1]);
      const lng = Number.parseFloat(parts[2]);
      if (!Number.isNaN(zoom) && !Number.isNaN(lat) && !Number.isNaN(lng)) {
        return { zoom, lat, lng };
      }
    }
    return null;
  }

  // Try hash first, then localStorage, then use default from map
  const position = parsePosition(location.hash)
    || parsePosition(localStorage.getItem("veggiekarte_map_position"));

  if (position) {
    map.setView([position.lat, position.lng], position.zoom);
  }

  map.on("moveend", setHash);

  /** Unlock marker hash and sync URL to current map position. */
  function syncHash() {
    markerHashActive = false;
    setHash();
  }

  return { syncHash };
}
